import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";

export default function Login() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error("Please enter your email");
            return;
        }
        if (!password) {
            toast.error("Please enter your password");
            return;
        }
        setLoading(true);
        try {
            const res = await api.login({ email, password, remember });
            switch (res.kind) {
                case "success":
                    toast.success(`Welcome back, ${res.name}`);
                    navigate("/shop");
                    break;
                case "unverified":
                    toast.warning(res.message || "Please verify your email first");
                    break;
                case "inactive":
                    toast.error(res.message || "Your account is inactive. Contact support.");
                    break;
                case "validation_error":
                    toast.error(res.message || "Invalid request");
                    break;
                case "auth_error":
                    toast.error(res.message || "Invalid login credentials");
                    break;
                default:
                    toast.error(res.message || "Login failed. Please try again.");
            }
        } catch (err) {
            toast.error(err?.message || "Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center px-4 py-10 fade-in"
            data-testid="login-page"
        >
            <div className="mb-8">
                <Logo size={56} />
            </div>

            <div
                className="ap-card w-full max-w-md p-8 md:p-10 scale-in"
                style={{ boxShadow: "0 30px 80px -40px rgba(0,0,0,0.8)" }}
            >
                <div className="ap-label mb-3" style={{ color: "var(--ap-blue-2)" }}>
                    Auto Shop Owner / Manager
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
                    Welcome back
                </h1>
                <p className="text-sm mb-8" style={{ color: "var(--ap-text-dim)" }}>
                    Sign in to access your admin dashboard
                </p>

                <form onSubmit={submit} className="flex flex-col gap-5" data-testid="login-form">
                    <div>
                        <label className="ap-label block mb-2">Email address</label>
                        <input
                            data-testid="login-email-input"
                            type="email"
                            className="ap-input"
                            placeholder="owner@autoshop.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                        />
                    </div>

                    <div>
                        <label className="ap-label block mb-2">Password</label>
                        <div className="relative">
                            <input
                                data-testid="login-password-input"
                                type={show ? "text" : "password"}
                                className="ap-input pr-12"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                            />
                            <button
                                data-testid="toggle-password-visibility"
                                type="button"
                                onClick={() => setShow(!show)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-200"
                            >
                                {show ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <label
                        className="flex items-center gap-3 select-none cursor-pointer text-sm"
                        data-testid="remember-me-toggle"
                    >
                        <input
                            type="checkbox"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                            className="w-4 h-4 accent-blue-600"
                        />
                        Remember me
                    </label>

                    <button
                        data-testid="login-submit-button"
                        type="submit"
                        disabled={loading}
                        className="ap-btn-primary w-full text-base"
                    >
                        {loading ? "Signing in..." : "Sign in"}
                    </button>
                </form>

                <div
                    className="mt-8 pt-6 border-t text-xs leading-relaxed"
                    style={{ borderColor: "var(--ap-line)", color: "var(--ap-text-dim)" }}
                >
                    Auto Shop Owner / Manager portal · Manage shop details, subscriptions and
                    payments. Need help? Contact your AutoPilot account manager.
                </div>
            </div>
        </div>
    );
}
